import { ApiVersions } from '@gonfalon/openapi/api-versions';
import { getClient } from '@gonfalon/openapi/client';
import { createIntegrationConfiguration } from '@gonfalon/openapi/operations/createIntegrationConfiguration';
import { deleteIntegrationConfiguration } from '@gonfalon/openapi/operations/deleteIntegrationConfiguration';
import { getAllIntegrationConfigurations } from '@gonfalon/openapi/operations/getAllIntegrationConfigurations';
import { getIntegrationConfiguration } from '@gonfalon/openapi/operations/getIntegrationConfiguration';
import { updateIntegrationConfiguration } from '@gonfalon/openapi/operations/updateIntegrationConfiguration';
import type { schemas } from '@gonfalon/openapi/types';
import { getQueryClient } from '@gonfalon/react-query-client/getQueryClient';
import { useMutation } from '@tanstack/react-query';
import type { FetchResponse } from 'openapi-fetch';
import type { MediaType } from 'openapi-typescript-helpers';

import { createMutationHook } from '../internal/createMutationHook';
import { createQueryOptions } from '../internal/createQueryOptions';
import { reactQueryResponseAdapter } from '../internal/reactQueryResponseAdapter';

export const integrationConfigurationQuery = createQueryOptions(getIntegrationConfiguration);
export const integrationConfigurationsQuery = createQueryOptions(getAllIntegrationConfigurations);

export const useCreateIntegrationConfiguration = createMutationHook(createIntegrationConfiguration, {
  onSuccess: async (data) => {
    const queryClient = getQueryClient();
    // Seed the detail query with the create response before navigating to the
    // edit page. The response contains server-assigned fields such as
    // _maintainerId; relying only on a list invalidation can briefly render a
    // stale config without its maintainer until the page is refreshed.
    queryClient.setQueryData(integrationConfigurationQuery({ integrationConfigurationId: data._id }).queryKey, data);
    await queryClient.invalidateQueries({ queryKey: integrationConfigurationsQuery.partialQueryKey() });
  },
});

export const useUpdateIntegrationConfiguration = createMutationHook(updateIntegrationConfiguration, {
  onSuccess: async (data, { integrationConfigurationId }) => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({
      queryKey: integrationConfigurationQuery.partialQueryKey({ integrationConfigurationId }),
    });
    await queryClient.invalidateQueries({ queryKey: integrationConfigurationsQuery.partialQueryKey() });
  },
});

export const useDeleteIntegrationConfiguration = createMutationHook(deleteIntegrationConfiguration, {
  onSuccess: async (data, { integrationConfigurationId }) => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({
      queryKey: integrationConfigurationQuery.partialQueryKey({ integrationConfigurationId }),
    });
    await queryClient.invalidateQueries({ queryKey: integrationConfigurationsQuery.partialQueryKey() });
  },
});

type IntegrationConfigurationResponse = schemas['IntegrationConfigurationsRep'];
type SnowflakeSetupBody = Pick<schemas['IntegrationConfigurationPost'], 'name' | 'tags' | 'configValues'>;
type SnowflakeSetupResponse = FetchResponse<Record<string | number, unknown>, unknown, MediaType>;
type PostSnowflakeSetup = (
  path: string,
  init?: { body?: unknown; headers?: HeadersInit },
) => Promise<SnowflakeSetupResponse>;

async function setupSnowflakeExperimentationIntegrationConfiguration({
  body,
}: {
  body: SnowflakeSetupBody;
}): Promise<SnowflakeSetupResponse> {
  const post = (getClient() as unknown as { POST: PostSnowflakeSetup }).POST;
  return post('/api/v2/integration-configurations/keys/snowflake-experimentation/setup', {
    body,
    headers: {
      'Content-Type': 'application/json',
      'LD-API-Version': ApiVersions.Beta,
    },
  });
}

async function completeSnowflakeExperimentationSetup({
  integrationConfigurationId,
}: {
  integrationConfigurationId: string;
}): Promise<SnowflakeSetupResponse> {
  const post = (getClient() as unknown as { POST: PostSnowflakeSetup }).POST;
  return post(`/api/v2/integration-configurations/${integrationConfigurationId}/complete-setup`, {
    body: {},
    headers: {
      'Content-Type': 'application/json',
      'LD-API-Version': ApiVersions.Beta,
    },
  });
}

export const useSetupSnowflakeExperimentationIntegrationConfiguration = () => {
  return useMutation({
    mutationFn: async (input: { body: SnowflakeSetupBody }) =>
      (await reactQueryResponseAdapter(
        setupSnowflakeExperimentationIntegrationConfiguration(input),
      )) as IntegrationConfigurationResponse,
    onSuccess: async () => {
      const queryClient = getQueryClient();
      await queryClient.invalidateQueries({ queryKey: integrationConfigurationsQuery.partialQueryKey() });
    },
  });
};

export const useCompleteSnowflakeExperimentationSetup = () => {
  return useMutation({
    mutationFn: async (input: { integrationConfigurationId: string }) =>
      (await reactQueryResponseAdapter(
        completeSnowflakeExperimentationSetup(input),
      )) as IntegrationConfigurationResponse,
    onSuccess: async (data, { integrationConfigurationId }) => {
      const queryClient = getQueryClient();
      // Completion returns the canonical config, including its server-assigned
      // maintainer. Seed the edit-page query directly so an immediate route
      // transition cannot observe a stale pre-completion representation.
      queryClient.setQueryData(integrationConfigurationQuery({ integrationConfigurationId }).queryKey, data);
      await queryClient.invalidateQueries({ queryKey: integrationConfigurationsQuery.partialQueryKey() });
    },
  });
};
